import React from 'react'

// Constants
import { PLAN_TO_DURATION_LABEL } from 'modules/workspace/constants'

// Utils
import { formatPriceShort } from 'utils/money'
import dateFormat from 'utils/localizedDateFormat'
import { getDiscountError, getAppliedDiscount } from 'modules/workspace/utils'

// Components
import Spinner from 'components/Spinner'
import Button from 'components/Button'
import Discount from '../Discount'
import { pluralize } from 'components/Pluralize'

// Styles
import {
  Wrapper,
  InnerWrapper,
  Item,
  TrialText,
  Section,
  HalfDivider,
  Paragraph,
  StrongText,
  SmallText,
} from './BillSimulation.styles'

// GQL
import {
  useDraftBillingSummaryQuery,
  useDraftBillingSummaryLazyQuery,
  BillingPlanFragment,
} from 'gqlTypes'

// Types
interface WorkspaceSubscribeSummaryProps {
  plan: BillingPlanFragment
  trialEnd: string
  numberEditors: number
  numberExtraSeats: number
  showDiscountInput?: boolean
  discountCode?: string
  onValidDiscountCode?: (discountCode: string) => void
  onClearDiscountCode?: () => void
}

// Helpers
const formatPrice = (price: number) =>
  formatPriceShort(price, { trimDecimalIfZero: false })

const buildDate = (date: string) => dateFormat(new Date(date))

const BillSimulation: React.FC<WorkspaceSubscribeSummaryProps> = ({
  plan,
  trialEnd,
  numberEditors,
  numberExtraSeats,
  showDiscountInput,
  onValidDiscountCode,
  onClearDiscountCode,
  discountCode,
}) => {
  /*
    Get Billing Summary
    If we stored a valid discount code in the state, this summary
    will return a CouponInfo with the valid coupon
  */
  const { loading, data, error, refetch } = useDraftBillingSummaryQuery({
    variables: {
      planId: plan.id,
      seats: numberEditors + numberExtraSeats,
      // Get the summary with the stored valid discount code
      promotionCode: discountCode,
    },
  })

  // The actual valid discount code
  const appliedDiscount = getAppliedDiscount(
    data?.draftBillingSummary?.couponInfo || undefined
  )

  // We first use this lazy query to preview if the discount is valid
  const [
    getSummaryPreviewWithDiscount,
    summaryPreviewWithDiscount,
  ] = useDraftBillingSummaryLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: draftBillingSummaryData => {
      onClearDiscountCode?.()

      const couponInfo =
        draftBillingSummaryData?.draftBillingSummary?.couponInfo

      if (couponInfo && !couponInfo.errorCode) {
        onValidDiscountCode?.(couponInfo.promotionCode)
      }
    },
  })

  const handleApplyDiscount = (discountCode: string) =>
    getSummaryPreviewWithDiscount({
      variables: {
        planId: plan.id,
        seats: numberEditors + numberExtraSeats,
        promotionCode: discountCode,
      },
    })

  // Discount error
  const discountError = getDiscountError(
    summaryPreviewWithDiscount.data?.draftBillingSummary?.couponInfo ||
      undefined
  )

  let children = null

  if (data && !error) {
    const { draftBillingSummary } = data
    const isOnTrial = new Date(trialEnd) > new Date()

    children = (
      <>
        {isOnTrial && (
          <TrialText>What you would pay after your trial ends</TrialText>
        )}

        <InnerWrapper>
          {showDiscountInput && onClearDiscountCode && (
            <Item marginBottom singleColumn>
              <Discount
                onApplyDiscountCode={handleApplyDiscount}
                onClearDiscountCode={onClearDiscountCode}
                appliedDiscountCodes={discountCode ? [discountCode] : []}
                error={discountError}
                isDisabled={loading || summaryPreviewWithDiscount.loading}
                isLoading={summaryPreviewWithDiscount.loading}
              />
            </Item>
          )}

          <Item marginBottom>
            <div>
              {numberEditors} {pluralize('Editor', 'Editors', numberEditors)}
            </div>
            <div>{formatPrice(plan.invoicePrice * numberEditors)}</div>
          </Item>

          {appliedDiscount && (
            <>
              <Item>
                <div>
                  {appliedDiscount.percentOff}% Off ({appliedDiscount.code})
                </div>
                <div>{formatPrice(-appliedDiscount.price)}</div>
              </Item>
              <Item singleColumn>
                <SmallText>
                  We’ve automatically applied this discount you previously used.
                  <br />
                  Valid on all workspaces until Dec 31 2020.
                </SmallText>
              </Item>
            </>
          )}
        </InnerWrapper>

        <Item>
          <StrongText>Total per Year</StrongText>
          <StrongText>
            {formatPrice(draftBillingSummary.billingCycleAmount)}
          </StrongText>
        </Item>

        {isOnTrial && (
          <Item>
            <SmallText>
              Due on {buildDate(trialEnd)}, then every{' '}
              {PLAN_TO_DURATION_LABEL[plan.type].toLowerCase()}
            </SmallText>
          </Item>
        )}
      </>
    )
  } else if (loading) {
    children = (
      <Section>
        <HalfDivider>
          <Paragraph>Loading your bill summary…</Paragraph>
          <Spinner primary />
        </HalfDivider>
      </Section>
    )
  } else {
    children = (
      <Section>
        <HalfDivider>
          <Paragraph>Sorry — we could not update your summary.</Paragraph>
          <Button variant="primary" loading={loading} onClick={() => refetch()}>
            Refresh
          </Button>
        </HalfDivider>
      </Section>
    )
  }

  return <Wrapper>{children}</Wrapper>
}

export default BillSimulation
